import CollapsibleCard from '@/components/shared/CollapsibleCard'
import { isReadOnly } from '@/lib/permissions'
import { formatDateTime } from '@/lib/utils'
import { useOrderHashesByNumberQuery } from '@/services/orders'
import { type PatientFile, usePatientFilesQuery } from '@/services/patient-files'
import { colors, sxPresets } from '@/theme'
import ImageIcon from '@mui/icons-material/Image'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Box, Button, Chip, CircularProgress, IconButton, Link, Paper, Tooltip, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import DeletePatientFileDialog from './DeletePatientFileDialog'
import PreviewPatientFileDialog from './PreviewPatientFileDialog'
import UploadPatientFileDialog from './UploadPatientFileDialog'

const formatFileSize = (sizeBytes: number | null): string | null => {
  if (sizeBytes == null) return null
  if (sizeBytes < 1024) return `${sizeBytes} B`
  if (sizeBytes < 1024 * 1024) return `${Math.round(sizeBytes / 1024)} KB`
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
}

const fileDisplayName = (file: PatientFile): string => file.originalFilename ?? `File ${file.id.slice(-6)}`

function FileThumbnail({ file, onClick }: { file: PatientFile; onClick: () => void }) {
  const [hasError, setHasError] = useState(false)
  const isImage = file.mimeType.startsWith('image/')

  const sx = {
    width: 56,
    height: 56,
    borderRadius: 1,
    border: `1px solid ${colors.border.default}`,
    bgcolor: colors.background.surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
    cursor: 'pointer'
  } as const

  if (isImage && !hasError) {
    return (
      <Box sx={sx} onClick={onClick}>
        <Box
          component='img'
          src={file.url}
          alt={fileDisplayName(file)}
          onError={() => setHasError(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>
    )
  }

  const Icon = file.mimeType === 'application/pdf' ? PictureAsPdfIcon : ImageIcon
  return (
    <Box sx={sx} onClick={onClick}>
      <Icon sx={{ color: colors.text.secondary }} />
    </Box>
  )
}

/**
 * Order context only: the patient's files that aren't attached to this order are still
 * reachable, so point at the patient page rather than dead-ending on "no files".
 */
function OtherPatientFilesHint({
  count,
  patientHash,
  more
}: {
  count: number
  patientHash: string
  /** True when files are already listed above, making these the additional ones. */
  more: boolean
}) {
  const label = `${count} ${more ? 'more ' : ''}${count === 1 ? 'file' : 'files'} for this patient`

  return (
    <Typography variant='smallText'>
      {label} —{' '}
      <Link component={RouterLink} to={`/patients/${patientHash}#patient-files`} sx={{ color: colors.brand.primary }}>
        view all on the patient page
      </Link>
    </Typography>
  )
}

interface PatientFilesSectionProps {
  patientHash: string
  /**
   * Order context (e.g. the med-request page): the list is filtered to files
   * attached to this order and uploads are pre-attached to it.
   */
  orderNumber?: string
  /** Hash of `orderNumber`, when the caller already has it — saves resolving it again. */
  orderHash?: string
  /** The patient's known order numbers, offered as suggestions in the upload dialog. */
  orderNumbers?: string[]
}

export default function PatientFilesSection({
  patientHash,
  orderNumber,
  orderHash,
  orderNumbers
}: PatientFilesSectionProps) {
  const readOnly = isReadOnly()
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [filePendingDelete, setFilePendingDelete] = useState<PatientFile | null>(null)
  const [previewFile, setPreviewFile] = useState<PatientFile | null>(null)

  const { data: files, isLoading, isError } = usePatientFilesQuery(patientHash)

  const visibleFiles = useMemo(() => {
    if (!files) return []
    if (!orderNumber) return files
    return files.filter(file => file.orderNumber === orderNumber)
  }, [files, orderNumber])

  // The feed carries order numbers, not hashes, so the order chips need a lookup to
  // link anywhere. The med-request page already knows its own order's hash and hands
  // it over, which leaves that page with nothing to resolve.
  const preResolvedNumber = orderHash ? orderNumber : undefined
  const unresolvedOrderNumbers = useMemo(
    () => visibleFiles.map(file => file.orderNumber).filter((n): n is string => !!n && n !== preResolvedNumber),
    [visibleFiles, preResolvedNumber]
  )
  const resolvedOrderHashes = useOrderHashesByNumberQuery(unresolvedOrderNumbers)

  const orderHashFor = (number: string | null): string | undefined => {
    if (!number) return undefined
    return number === preResolvedNumber ? orderHash : resolvedOrderHashes.get(number)
  }

  // In order context, files of the patient not attached to this order live on the patient page.
  const otherFilesCount = (files?.length ?? 0) - visibleFiles.length
  const showOtherFilesHint = !!orderNumber && otherFilesCount > 0

  const isEmpty = !isLoading && !isError && visibleFiles.length === 0
  const emptyMessage = orderNumber ? 'No files attached to this order' : 'No files uploaded for this patient'

  return (
    <>
      <CollapsibleCard
        sectionId={orderNumber ? 'order-files' : 'patient-files'}
        title={orderNumber ? 'Order files' : 'Patient files'}
        isEmpty={isEmpty}
        emptyMessage={emptyMessage}
        // An order with no files of its own is not a dead end while the patient has others.
        emptyContent={
          showOtherFilesHint ? (
            <Box sx={{ ...sxPresets().section.content, p: 4, textAlign: 'center' }}>
              <Typography variant='body2'>{emptyMessage}</Typography>
              <Box sx={{ mt: 1 }}>
                <OtherPatientFilesHint count={otherFilesCount} patientHash={patientHash} more={false} />
              </Box>
            </Box>
          ) : undefined
        }
        actions={
          readOnly ? undefined : (
            <Button
              variant='secondary'
              size='small'
              startIcon={<UploadFileIcon />}
              onClick={() => setUploadDialogOpen(true)}
            >
              Upload file
            </Button>
          )
        }
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : isError ? (
          <Box sx={{ ...sxPresets().section.content, p: 4, textAlign: 'center' }}>
            <Typography variant='body2'>Files could not be loaded</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              ...sxPresets().section.content,
              bgcolor: colors.background.surface,
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            {visibleFiles.map(file => {
              const size = formatFileSize(file.sizeBytes)
              const fileOrderHash = orderHashFor(file.orderNumber)
              const meta = [
                file.uploadedBy === 'patient' ? 'Uploaded by patient' : 'Uploaded by support',
                formatDateTime(file.createdAt),
                size
              ]
                .filter(Boolean)
                .join(' · ')

              return (
                <Paper
                  key={file.id}
                  elevation={0}
                  sx={{
                    ...sxPresets().card.default,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2
                  }}
                >
                  <FileThumbnail key={file.url} file={file} onClick={() => setPreviewFile(file)} />

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Link
                      component='button'
                      type='button'
                      variant='fieldValue'
                      underline='always'
                      onClick={() => setPreviewFile(file)}
                      sx={{
                        display: 'block',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'left',
                        color: colors.text.primary,
                        textDecorationColor: 'inherit'
                      }}
                    >
                      {fileDisplayName(file)}
                    </Link>
                    <Typography variant='smallText'>{meta}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      {fileOrderHash ? (
                        <Link
                          component={RouterLink}
                          to={`/orders/${fileOrderHash}`}
                          underline='none'
                          sx={{ display: 'inline-flex' }}
                        >
                          {/* Brand-coloured so it reads as interactive next to the status chips —
                              an order chip whose hash didn't resolve stays plain grey. No
                              `clickable`: it would nest a role=button inside the link, giving
                              the one destination two tab stops. The anchor is the control. */}
                          <Chip
                            size='small'
                            variant='outlined'
                            label={`Order ${file.orderNumber}`}
                            sx={{
                              cursor: 'pointer',
                              color: colors.brand.primary,
                              borderColor: colors.brand.primary,
                              '&:hover': { bgcolor: colors.brand.primaryLight }
                            }}
                          />
                        </Link>
                      ) : (
                        <Chip
                          size='small'
                          variant='outlined'
                          label={file.orderNumber ? `Order ${file.orderNumber}` : 'No order'}
                        />
                      )}
                      {file.viewedByPrescriber ? (
                        <Chip size='small' color='success' variant='outlined' label='Viewed by prescriber' />
                      ) : (
                        <Chip size='small' variant='outlined' label='Not viewed by prescriber' />
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      flexShrink: 0
                    }}
                  >
                    <Tooltip title='View file'>
                      <IconButton size='small' onClick={() => setPreviewFile(file)}>
                        <VisibilityOutlinedIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    {!readOnly && (
                      <Tooltip title='Delete file'>
                        <IconButton size='small' onClick={() => setFilePendingDelete(file)}>
                          <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Paper>
              )
            })}
            {showOtherFilesHint && <OtherPatientFilesHint count={otherFilesCount} patientHash={patientHash} more />}
          </Box>
        )}
      </CollapsibleCard>

      <UploadPatientFileDialog
        open={uploadDialogOpen}
        patientHash={patientHash}
        orderNumber={orderNumber}
        orderNumbers={orderNumbers}
        onClose={() => setUploadDialogOpen(false)}
      />
      <DeletePatientFileDialog
        open={filePendingDelete != null}
        patientHash={patientHash}
        file={filePendingDelete}
        onClose={() => setFilePendingDelete(null)}
      />
      <PreviewPatientFileDialog
        open={previewFile != null}
        patientHash={patientHash}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </>
  )
}
